import React, { JSX } from 'react';
import { useNavigate } from 'react-router-dom'; // 1. Import useNavigate
import { 
  LayoutDashboard, Settings, ShieldCheck, Briefcase, Target, 
  Zap, BarChart3, Scale, MousePointer2, Clock, ArrowUpRight, Workflow,
  Layers 
} from 'lucide-react';

// --- TS INTERFACES ---
export interface AdminMenuItem {
  id?: string;
  adminMenuName: string;
  menuSlug: string;
  isDisplayOnUi?: boolean;
  status?: boolean;
}

interface MenuConfig {
  icon: JSX.Element;
  color: string;
  bg: string;
  border: string;
}

interface AdminDashboardMenuProps {
  data: AdminMenuItem[];
}

const AdminDashboardMenuComponent: React.FC<AdminDashboardMenuProps> = ({ data = [] }) => {
  const navigate = useNavigate(); // 2. Initialize navigate function

  const colorRotation = [
    { color: 'text-blue-600', bg: 'bg-blue-50', border: 'hover:border-blue-200' },
    { color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'hover:border-indigo-200' },
    { color: 'text-purple-600', bg: 'bg-purple-50', border: 'hover:border-purple-200' },
    { color: 'text-teal-600', bg: 'bg-teal-50', border: 'hover:border-teal-200' },
    { color: 'text-rose-600', bg: 'bg-rose-50', border: 'hover:border-rose-200' },
  ];

  const getMenuConfig = (slug: string, index: number): MenuConfig => {
    const configs: Record<string, MenuConfig> = {
      'architecture-experience': { icon: <LayoutDashboard />, color: 'text-blue-600', bg: 'bg-blue-50', border: 'hover:border-blue-200' },
      'hiring-authority': { icon: <ShieldCheck />, color: 'text-purple-600', bg: 'bg-purple-50', border: 'hover:border-purple-200' },
      'decision-speed': { icon: <Zap />, color: 'text-amber-500', bg: 'bg-amber-50', border: 'hover:border-amber-200' },
      'reputation-signals': { icon: <BarChart3 />, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'hover:border-emerald-200' },
      'owner-ship-level': { icon: <Target />, color: 'text-rose-600', bg: 'bg-rose-50', border: 'hover:border-rose-200' },
      'role-level': { icon: <Briefcase />, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'hover:border-indigo-200' },
      'business-impact': { icon: <Settings />, color: 'text-slate-600', bg: 'bg-slate-50', border: 'hover:border-slate-200' },
      'target-role': { icon: <MousePointer2 />, color: 'text-cyan-600', bg: 'bg-cyan-50', border: 'hover:border-cyan-200' },
      'startup-risk-preference': { icon: <Scale />, color: 'text-orange-600', bg: 'bg-orange-50', border: 'hover:border-orange-200' },
      'work-style': { icon: <Workflow />, color: 'text-violet-600', bg: 'bg-violet-50', border: 'hover:border-violet-200' },
      'product-scale': { icon: <BarChart3 />, color: 'text-pink-600', bg: 'bg-pink-50', border: 'hover:border-pink-200' },
      'work-flexibility': { icon: <Clock />, color: 'text-teal-600', bg: 'bg-teal-50', border: 'hover:border-teal-200' },
    };

    if (configs[slug]) {
      return configs[slug];
    } else {
      const dynamicTheme = colorRotation[index % colorRotation.length];
      return { icon: <Layers />, ...dynamicTheme };
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6 md:p-12">
      <div className="max-w-7xl mx-auto">
        <header className="mb-12">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">System Configuration</h1>
          <p className="text-slate-500 mt-2 font-medium">Manage and adjust internal company parameters dynamically.</p>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {data.map((item, index) => {
            const theme = getMenuConfig(item.menuSlug, index);
            
            return (
              <div
                key={item.id || item.menuSlug}
                // 3. Added onClick handler to trigger navigation
                onClick={() => navigate(`/admin/${item.menuSlug}`)} 
                className={`group bg-white border border-slate-100 rounded-[2rem] p-8 transition-all duration-300 
                hover:shadow-2xl hover:shadow-slate-200/50 hover:-translate-y-1 cursor-pointer ${theme.border}`}
              >
                <div className="relative flex flex-col h-full">
                  <div className={`w-14 h-14 ${theme.bg} ${theme.color} rounded-2xl flex items-center justify-center mb-6 
                    group-hover:rotate-6 transition-transform duration-300`}>
                    {React.cloneElement(theme.icon as React.ReactElement, { size: 28 } as any)}
                  </div>

                  <h3 className="text-lg font-bold text-slate-800 group-hover:text-indigo-600 transition-colors">
                    {item.adminMenuName}
                  </h3>

                  <div className="mt-2 flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${item.isDisplayOnUi ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {item.isDisplayOnUi ? 'Public' : 'Hidden'}
                    </span>
                  </div>

                  <div className="mt-8 pt-6 border-t border-slate-50 flex items-center justify-between group-hover:border-slate-100">
                    <span className="text-xs font-bold text-slate-400 group-hover:text-slate-600">Open Settings</span>
                    <ArrowUpRight size={18} className={`${theme.color} transition-transform group-hover:translate-x-1 group-hover:-translate-y-1`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardMenuComponent;